/**
 * Índice derivado da árvore.
 *
 * É o que substitui `getChildrenForTab`/`getChildrenForPanel`/`getChildrenOfRow`
 * do canvas antigo — funções chamadas de dentro de `*ngFor` aninhado, que
 * refaziam a hierarquia a cada ciclo de detecção de mudança.
 *
 * Aqui a hierarquia já está na árvore (`node.children`) e o índice é construído
 * uma vez por versão do documento. No editor vira `computed()`, memoizado por
 * identidade do objeto.
 */
import { ScreenDocument } from './screen-schema';
import { NodeType, ScreenNode } from './node-props';

/** Caminho até um nó, como índices de `children` desde a raiz. */
export type NodePath = readonly number[];

export interface ScreenIndex {
  readonly byId: ReadonlyMap<string, ScreenNode>;
  /** Id do pai, ou null na raiz. */
  readonly parentOf: ReadonlyMap<string, string | null>;
  readonly pathOf: ReadonlyMap<string, NodePath>;
  /** Nós que gravam valor, indexados por `binding.fieldName`. */
  readonly fieldsByName: ReadonlyMap<string, ScreenNode>;
  /** Nós que leem valor, indexados por `binding.dataPath`. */
  readonly nodesByDataPath: ReadonlyMap<string, readonly ScreenNode[]>;
  /** Ordem de visita em profundidade — útil para varreduras e validação. */
  readonly inOrder: readonly ScreenNode[];
}

export function buildIndex(doc: ScreenDocument): ScreenIndex {
  const byId = new Map<string, ScreenNode>();
  const parentOf = new Map<string, string | null>();
  const pathOf = new Map<string, NodePath>();
  const fieldsByName = new Map<string, ScreenNode>();
  const byDataPath = new Map<string, ScreenNode[]>();
  const inOrder: ScreenNode[] = [];

  const walk = (node: ScreenNode, parentId: string | null, path: NodePath): void => {
    byId.set(node.id, node);
    parentOf.set(node.id, parentId);
    pathOf.set(node.id, path);
    inOrder.push(node);

    const fieldName = node.binding?.fieldName;
    if (fieldName) {
      // Primeiro ganha: nome repetido é erro de configuração, e a validação
      // da tela reporta. O índice não é o lugar de decidir isso.
      if (!fieldsByName.has(fieldName)) {
        fieldsByName.set(fieldName, node);
      }
    }

    const dataPath = node.binding?.dataPath;
    if (dataPath) {
      const list = byDataPath.get(dataPath);
      if (list) {
        list.push(node);
      } else {
        byDataPath.set(dataPath, [node]);
      }
    }

    const children = node.children;
    if (children) {
      for (let i = 0; i < children.length; i++) {
        walk(children[i], node.id, [...path, i]);
      }
    }
  };

  walk(doc.root, null, []);

  return {
    byId,
    parentOf,
    pathOf,
    fieldsByName,
    nodesByDataPath: byDataPath,
    inOrder
  };
}

// ---------------------------------------------------------------- consultas

export function getNode(index: ScreenIndex, id: string): ScreenNode | undefined {
  return index.byId.get(id);
}

export function getParent(index: ScreenIndex, id: string): ScreenNode | undefined {
  const parentId = index.parentOf.get(id);
  return parentId ? index.byId.get(parentId) : undefined;
}

/** Ancestrais, do pai até a raiz. */
export function getAncestors(index: ScreenIndex, id: string): readonly ScreenNode[] {
  const out: ScreenNode[] = [];
  let current = index.parentOf.get(id) ?? null;
  while (current) {
    const node = index.byId.get(current);
    if (!node) break;
    out.push(node);
    current = index.parentOf.get(current) ?? null;
  }
  return out;
}

/** Primeiro ancestral de um dos tipos dados. Ex.: a aba que contém um campo. */
export function findAncestorOfType(
  index: ScreenIndex,
  id: string,
  types: readonly NodeType[]
): ScreenNode | undefined {
  return getAncestors(index, id).find(n => types.includes(n.type));
}

/** Todos os descendentes de um nó, em profundidade. */
export function getDescendants(node: ScreenNode): readonly ScreenNode[] {
  const out: ScreenNode[] = [];
  const stack: ScreenNode[] = [...(node.children ?? [])];
  while (stack.length) {
    const current = stack.pop()!;
    out.push(current);
    if (current.children) stack.push(...current.children);
  }
  return out;
}

export function nodesOfType<K extends NodeType>(
  index: ScreenIndex,
  type: K
): readonly ScreenNode<K>[] {
  // predicado em vez de cast: a união distributiva de ScreenNode nao aceita
  // conversao direta de ScreenNode[] para ScreenNode<K>[]
  return index.inOrder.filter((n): n is ScreenNode<K> => n.type === type);
}

/** `trackBy` do `*ngFor`: sem isto o Angular recria a árvore a cada mudança. */
export function trackByNodeId(_: number, node: ScreenNode): string {
  return node.id;
}
